'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Timer, Target, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import FallingManatees from './FallingManatees';

interface Hotspot {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface ManateeGamePhaseProps {
  onComplete: (timeMs: number | null, correct: boolean) => void;
  entryId: string | null;
}

type GameState = 'loading' | 'countdown' | 'playing' | 'correct' | 'wrong';

const REVEAL_SECONDS = 5; // seconds to show image full screen before game starts

export default function ManateeGamePhase({ onComplete, entryId }: ManateeGamePhaseProps) {
  const [gameState, setGameState] = useState<GameState>('loading');
  const [elapsedMs, setElapsedMs] = useState(0);
  const [imageSrc, setImageSrc] = useState<string>('');
  const [hotspot, setHotspot] = useState<Hotspot | null>(null);
  const [clickPos, setClickPos] = useState<{ x: number; y: number } | null>(null);
  const [revealSecondsLeft, setRevealSecondsLeft] = useState(REVEAL_SECONDS);
  const startTimeRef = useRef<number>(0);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const revealIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const imageContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    import('@/lib/services/gameImageService').then(({ gameImageService, MANATEE_IMAGE_ID }) => {
      gameImageService.getImage(MANATEE_IMAGE_ID).then((record) => {
        if (record?.imageUrl) {
          setImageSrc(record.imageUrl);
          if (record.hotspotData) setHotspot(record.hotspotData);
          // Start fullscreen reveal countdown
          setGameState('countdown');
          setRevealSecondsLeft(REVEAL_SECONDS);

          revealIntervalRef.current = setInterval(() => {
            setRevealSecondsLeft((prev) => {
              if (prev <= 1) {
                clearInterval(revealIntervalRef.current!);
                // Transition directly to playing — stay full screen
                setGameState('playing');
                startTimeRef.current = Date.now();
                intervalRef.current = setInterval(() => {
                  setElapsedMs(Date.now() - startTimeRef.current);
                }, 50);
                return 0;
              }
              return prev - 1;
            });
          }, 1000);
        } else {
          setGameState('playing');
          startTimeRef.current = Date.now();
          intervalRef.current = setInterval(() => {
            setElapsedMs(Date.now() - startTimeRef.current);
          }, 50);
        }
      });
    });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (revealIntervalRef.current) clearInterval(revealIntervalRef.current);
    };
  }, []);

  const handleImageClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (gameState !== 'playing') return;
    if (intervalRef.current) clearInterval(intervalRef.current);

    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = ((e.clientX - rect.left) / rect.width) * 100;
    const clickY = ((e.clientY - rect.top) / rect.height) * 100;
    const finalTime = Date.now() - startTimeRef.current;

    setClickPos({ x: clickX, y: clickY });
    setElapsedMs(finalTime);

    let correct = false;
    if (hotspot) {
      correct =
        clickX >= hotspot.x &&
        clickX <= hotspot.x + hotspot.width &&
        clickY >= hotspot.y &&
        clickY <= hotspot.y + hotspot.height;
    }

    setGameState(correct ? 'correct' : 'wrong');

    setTimeout(() => {
      onComplete(correct ? finalTime : null, correct);
    }, 2500);
  }, [gameState, hotspot, onComplete]);

  const formatTime = (ms: number) => {
    const s = Math.floor(ms / 1000);
    const centis = Math.floor((ms % 1000) / 10);
    return `${s}.${centis.toString().padStart(2, '0')}s`;
  };

  if (gameState === 'loading') {
    return (
      <div className="animate-fade-in bg-white rounded-3xl shadow-xl border border-blue-100 p-8 text-center">
        <Loader2 size={32} className="animate-spin text-blue-400 mx-auto mb-4" />
        <p className="text-gray-500 text-sm">Loading game...</p>
      </div>
    );
  }

  if (!imageSrc && gameState !== 'loading') {
    return (
      <div className="animate-fade-in bg-white rounded-3xl shadow-xl border border-blue-100 p-8 text-center">
        <div className="text-5xl mb-4">🦭</div>
        <h2 className="text-xl font-bold text-gray-700 mb-2">Game Not Set Up Yet</h2>
        <p className="text-gray-400 text-sm">The admin hasn&apos;t uploaded the manatee image yet. Check back soon!</p>
      </div>
    );
  }

  // Full screen for countdown AND playing AND result states
  if (gameState === 'countdown' || gameState === 'playing' || gameState === 'correct' || gameState === 'wrong') {
    const isPlaying = gameState === 'playing';
    const isCountdown = gameState === 'countdown';
    const isCorrect = gameState === 'correct';
    const isWrong = gameState === 'wrong';

    return (
      <div className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center">
        <FallingManatees active={isCountdown || isPlaying} />

        {/* Top bar */}
        <div className="absolute top-0 left-0 right-0 z-10 p-4 text-center">
          {isCountdown && (
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-3">
              <span className="text-white font-bold text-lg">🦭 Find the Manatee in the 3T shirt!</span>
              <span className="text-yellow-300 font-extrabold text-2xl font-tabular">{revealSecondsLeft}s</span>
            </div>
          )}
          {isPlaying && (
            <div className="inline-flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-2xl px-6 py-3">
              <span className="text-white font-bold text-lg">🦭 Click where the Manatee is hiding!</span>
              <div className={`flex items-center gap-2 px-4 py-1.5 rounded-xl font-extrabold text-xl font-tabular bg-white/20 text-white`}>
                <Timer size={18} />
                {formatTime(elapsedMs)}
              </div>
            </div>
          )}
          {(isCorrect || isWrong) && (
            <div className={`inline-flex items-center gap-3 backdrop-blur-sm rounded-2xl px-6 py-3 ${isCorrect ? 'bg-green-500/80' : 'bg-red-500/80'}`}>
              {isCorrect ? <CheckCircle size={22} className="text-white" /> : <XCircle size={22} className="text-white" />}
              <span className="text-white font-bold text-lg">
                {isCorrect ? `Found it! 🎉 Time: ${formatTime(elapsedMs)}` : `Not quite! ⏱ Time: ${formatTime(elapsedMs)}`}
              </span>
            </div>
          )}
          {isCountdown && (
            <p className="text-white/60 text-sm mt-2">Study the image — then click where the manatee is hiding!</p>
          )}
          {isPlaying && (
            <p className="text-white/60 text-sm mt-2">Click directly on the manatee wearing the 3T shirt</p>
          )}
        </div>

        {/* Full screen image — clickable during playing */}
        <div
          ref={imageContainerRef}
          className={`w-full h-full relative ${isPlaying ? 'cursor-crosshair' : 'cursor-default'}`}
          onClick={isPlaying ? handleImageClick : undefined}
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={imageSrc}
            alt="Find the manatee wearing the 3T shirt"
            className="w-full h-full object-contain pointer-events-none"
            draggable={false}
          />

          {/* Click marker */}
          {clickPos && (
            <div
              className="absolute w-10 h-10 -translate-x-1/2 -translate-y-1/2 pointer-events-none"
              style={{ left: `${clickPos.x}%`, top: `${clickPos.y}%` }}
            >
              <div className={`w-10 h-10 rounded-full border-4 flex items-center justify-center ${
                isCorrect ? 'border-green-400 bg-green-400/30' : 'border-red-400 bg-red-400/30'
              }`}>
                <Target size={16} className={isCorrect ? 'text-green-300' : 'text-red-300'} />
              </div>
            </div>
          )}

          {/* Hotspot reveal on correct */}
          {isCorrect && hotspot && (
            <div
              className="absolute border-4 border-green-400 bg-green-400/20 rounded-xl pointer-events-none animate-pulse"
              style={{
                left: `${hotspot.x}%`,
                top: `${hotspot.y}%`,
                width: `${hotspot.width}%`,
                height: `${hotspot.height}%`,
              }}
            />
          )}

          {/* Hotspot reveal on wrong */}
          {isWrong && hotspot && (
            <div
              className="absolute border-4 border-green-400 bg-green-400/20 rounded-xl pointer-events-none"
              style={{
                left: `${hotspot.x}%`,
                top: `${hotspot.y}%`,
                width: `${hotspot.width}%`,
                height: `${hotspot.height}%`,
              }}
            >
              <div className="absolute -top-8 left-1/2 -translate-x-1/2 bg-green-600 text-white text-sm font-bold px-3 py-1.5 rounded-full whitespace-nowrap shadow-lg">
                Here it was! 🦭
              </div>
            </div>
          )}
        </div>

        {isCountdown && (
          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-white/50 text-xs z-10">
            Game starts when timer ends
          </div>
        )}
      </div>
    );
  }

  return null;
}
